import React, { useState, useEffect } from 'react';
import { Container, Card, Row, Col } from 'react-bootstrap';
import AsyncSelect from 'react-select/async';
import { toast } from 'react-toastify';
import Header from '~/Components/Header/Header';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import * as api from '~/Services/Service';
import PainelAlternativo from './components/PainelAlternativo';

// W-0503 — GES Matricial de Despesas — Usuário Alternativo
// ADR-002: painel único alternante (sem modal de cadastro, sem tabs)
// ADR-001: namespace separado — não reusar ControleRenegociacoes/ContasSalario

const carregarUsuariosTitular = (inputValue, callback) => {
  api
    .buscarUsuarioOptions('ativos')
    .then((res) => {
      const lista = res || [];
      const filtrados = inputValue
        ? lista.filter((u) => {
            const nome = u.nome_completo || u.nome || '';
            return nome.toLowerCase().includes(inputValue.toLowerCase());
          })
        : lista;
      callback(
        filtrados.slice(0, 50).map((u) => ({
          value: u.codigo_usuario,
          label: `${u.codigo_usuario} - ${u.nome_completo || u.nome || ''}`,
        }))
      );
    })
    .catch(() => callback([]));
};

const UsuarioAlternativo = () => {
  const [titularSelecionado, setTitularSelecionado] = useState(null);
  const [vigente, setVigente] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(null); // 'cadastrar' | 'inativar' | null
  const [pendingFormValues, setPendingFormValues] = useState(null);

  useEffect(() => {
    if (!titularSelecionado) {
      setVigente(null);
      return;
    }
    buscarVigente(titularSelecionado.codigo_usuario);
  }, [titularSelecionado]);

  const buscarVigente = (codigoUsuario) => {
    setLoading(true);
    api
      .buscarMatricialUsuarioAlternativoVigente(codigoUsuario)
      .then((res) => {
        setVigente(res != null ? res : null);
      })
      .catch((err) => {
        const msg =
          err && err.message ? err.message : 'Erro ao buscar alternativo vigente.';
        toast.error(msg);
        setVigente(null);
      })
      .finally(() => setLoading(false));
  };

  const handleTitularChange = (option) => {
    if (!option) {
      setTitularSelecionado(null);
      setVigente(null);
      return;
    }
    const partes = option.label.split(' - ');
    setTitularSelecionado({
      codigo_usuario: option.value,
      nome_completo: partes.slice(1).join(' - '),
    });
  };

  const handleDefinir = (formValues) => {
    setPendingFormValues(formValues);
    setConfirmOpen('cadastrar');
  };

  const handleInativar = () => {
    setConfirmOpen('inativar');
  };

  const handleConfirmClose = (confirmado) => {
    if (!confirmado) {
      setConfirmOpen(null);
      setPendingFormValues(null);
      return;
    }

    if (confirmOpen === 'cadastrar') {
      setConfirmOpen(null);
      executarDefinir();
    } else if (confirmOpen === 'inativar') {
      setConfirmOpen(null);
      executarInativar();
    }
  };

  const executarDefinir = () => {
    if (!titularSelecionado || !pendingFormValues) return;
    setLoading(true);
    const req = {
      codigo_usuario_titular: titularSelecionado.codigo_usuario,
      codigo_usuario_alternativo: pendingFormValues.codigoUsuarioAlternativo,
      data_inicio: pendingFormValues.dataInicio,
      data_fim: pendingFormValues.dataFim,
    };
    api
      .definirMatricialUsuarioAlternativo(req)
      .then(() => {
        toast.success('Usuário alternativo cadastrado.');
        setPendingFormValues(null);
        buscarVigente(titularSelecionado.codigo_usuario);
      })
      .catch((err) => {
        const msg =
          err && err.message ? err.message : 'Erro ao cadastrar usuário alternativo.';
        toast.error(msg);
        setLoading(false);
      });
  };

  const executarInativar = () => {
    if (!vigente) return;
    setLoading(true);
    api
      .inativarMatricialUsuarioAlternativo(vigente.codigo)
      .then(() => {
        toast.success('Usuário alternativo inativado.');
        setVigente(null);
        setLoading(false);
      })
      .catch((err) => {
        const msg =
          err && err.message ? err.message : 'Erro ao inativar usuário alternativo.';
        toast.error(msg);
        setLoading(false);
      });
  };

  const confirmTitle =
    confirmOpen === 'cadastrar' ? 'Confirmar Cadastro' : 'Confirmar Inativação';
  const confirmMessage =
    confirmOpen === 'cadastrar'
      ? 'Deseja cadastrar?'
      : 'Deseja inativar o usuário alternativo?';

  return (
    <>
      <Header title="Usuário Alternativo — Matricial de Despesas" />
      <Container fluid className="mt-3">
        <Card>
          <Card.Body>
            <Row>
              <Col md={6}>
                <p className="mb-1 font-weight-bold">
                  Usuário Titular <span className="text-danger">*</span>
                </p>
                <AsyncSelect
                  cacheOptions
                  defaultOptions
                  loadOptions={carregarUsuariosTitular}
                  placeholder="Pesquisar titular..."
                  loadingMessage={() => 'Carregando...'}
                  noOptionsMessage={() => 'Nenhum usuário encontrado'}
                  onChange={handleTitularChange}
                  value={
                    titularSelecionado
                      ? {
                          value: titularSelecionado.codigo_usuario,
                          label: `${titularSelecionado.codigo_usuario} - ${titularSelecionado.nome_completo}`,
                        }
                      : null
                  }
                  isClearable
                  components={{ DropdownIndicator }}
                  styles={customStyles}
                />
              </Col>
            </Row>

            <PainelAlternativo
              titular={titularSelecionado}
              vigente={vigente}
              onDefinir={handleDefinir}
              onInativar={handleInativar}
              loading={loading}
            />
          </Card.Body>
        </Card>
      </Container>

      <ModalConfirmacao
        show={confirmOpen !== null}
        onClose={handleConfirmClose}
        title={confirmTitle}
        message={confirmMessage}
        variant={confirmOpen === 'inativar' ? 'danger' : 'success'}
      />
    </>
  );
};

export default UsuarioAlternativo;
